import { useEffect } from "react";
import { create } from "zustand";
import { useLocation } from "wouter";
import i18n from "../i18n";
import {
  sendAssistantMessage,
  listConversations,
  getConversation,
  deleteConversation,
  flagConversation,
} from "./assistantApi";

const getInitialState = () => ({
  isOpen: false,
  messages: [],
  conversationId: null,
  isFlagged: false,
  isLoading: false,
  currentEntityName: null,
  conversations: [],
});

export const useAssistantStore = create((set, get) => ({
  ...getInitialState(),

  togglePanel: () => set((state) => ({ isOpen: !state.isOpen })),
  openPanel: () => set({ isOpen: true }),
  closePanel: () => set({ isOpen: false }),

  setCurrentEntityName: (name) => set({ currentEntityName: name }),

  clearConversation: () =>
    set({
      messages: [],
      conversationId: null,
      isFlagged: false,
      isLoading: false,
    }),

  reset: () => set(getInitialState()),

  loadConversations: async () => {
    const list = await listConversations();
    set({ conversations: list || [] });
  },

  loadConversation: async (id) => {
    const data = await getConversation(id);
    if (!data) return;
    const messages = (data.messages || [])
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, content: m.content }));
    set({
      messages,
      conversationId: data.id,
      isFlagged: !!data.isFlagged,
      isLoading: false,
    });
  },

  deleteConversation: async (id) => {
    await deleteConversation(id);
    set((state) => {
      const conversations = state.conversations.filter((c) => c.id !== id);
      const isCurrent = state.conversationId === id;
      return isCurrent
        ? {
            conversations,
            messages: [],
            conversationId: null,
            isFlagged: false,
            isLoading: false,
          }
        : { conversations };
    });
  },

  flagConversation: async (reason) => {
    const { conversationId } = get();
    if (!conversationId) return;
    await flagConversation(conversationId, reason);
    set((state) => ({
      isFlagged: true,
      conversations: state.conversations.map((c) =>
        c.id === conversationId ? { ...c, isFlagged: true } : c,
      ),
    }));
  },

  sendMessage: async (text, pageContext) => {
    const { conversationId, messages } = get();

    // Add user message immediately
    set({
      messages: [...messages, { role: "user", content: text }],
      isLoading: true,
    });

    let assistantContent = "";
    let toolStatus = [];
    let newConversationId = conversationId;

    try {
      await sendAssistantMessage(text, conversationId, pageContext, (chunk) => {
        switch (chunk.type) {
          case "conversation":
            newConversationId = chunk.conversationId;
            set({ conversationId: chunk.conversationId });
            break;

          case "thinking":
            toolStatus = [...toolStatus, { tool: chunk.tool, status: "running" }];
            set((state) => ({
              messages: updateLastAssistantMessage(state.messages, {
                role: "assistant",
                content: assistantContent,
                toolStatus: [...toolStatus],
                isStreaming: true,
              }),
            }));
            break;

          case "tool_result":
            toolStatus = toolStatus.map((t) =>
              t.tool === chunk.tool ? { ...t, status: "done", summary: chunk.content } : t
            );
            set((state) => ({
              messages: updateLastAssistantMessage(state.messages, {
                role: "assistant",
                content: assistantContent,
                toolStatus: [...toolStatus],
                isStreaming: true,
              }),
            }));
            break;

          case "text":
            assistantContent = chunk.content;
            set((state) => ({
              messages: updateLastAssistantMessage(state.messages, {
                role: "assistant",
                content: assistantContent,
                toolStatus: [...toolStatus],
                isStreaming: false,
              }),
            }));
            break;

          case "done":
            set({ isLoading: false });
            break;
        }
      });
    } catch (error) {
      console.error("Assistant error:", error);
      set((state) => ({
        messages: [
          ...state.messages,
          { role: "assistant", content: i18n.t("assistant.error") },
        ],
      }));
    } finally {
      set({ isLoading: false });
    }
  },
}));

function updateLastAssistantMessage(messages, newMsg) {
  const lastIdx = messages.length - 1;
  if (lastIdx >= 0 && messages[lastIdx].role === "assistant") {
    return [...messages.slice(0, lastIdx), newMsg];
  }
  return [...messages, newMsg];
}

/** Extract page context from the current URL */
export function usePageContext() {
  const [location] = useLocation();
  const entityName = useAssistantStore((s) => s.currentEntityName);

  const productMatch = location.match(/\/pim\/products\/([a-f0-9]{24})/);
  if (productMatch) {
    return {
      currentPage: location,
      entityType: "product",
      entityId: productMatch[1],
      entityName,
    };
  }

  const schemaMatch = location.match(/\/pim\/schemas\/([a-f0-9]{24})/);
  if (schemaMatch) {
    return {
      currentPage: location,
      entityType: "schema",
      entityId: schemaMatch[1],
      entityName,
    };
  }

  const importMatch = location.match(/\/imports\/([a-f0-9]{24})/);
  if (importMatch) {
    return {
      currentPage: location,
      entityType: "import",
      entityId: importMatch[1],
      entityName,
    };
  }

  return { currentPage: location };
}

/**
 * Register the current page's entity name with the assistant. Pages that load
 * a single entity (product, schema, import) call this once the name is known;
 * the assistant then refers to it by name without an extra fetch. The name is
 * cleared on unmount.
 */
export function useAssistantEntityName(name) {
  const setCurrentEntityName = useAssistantStore((s) => s.setCurrentEntityName);
  useEffect(() => {
    if (!name) return;
    setCurrentEntityName(name);
    return () => setCurrentEntityName(null);
  }, [name, setCurrentEntityName]);
}
